import type { RaceId, RaceView } from '@ps/isp-web-services-types';
import { sortBy } from 'lodash';
import type { Selector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { withMeetingBasedMarketTemplateModelSelectors } from '@swf/sports/models/market-templates/meetingBased';
import type { MeetingBasedMarketTemplatesView } from '@swf/sports/types/templates';

import { defaultState } from './defaultState';
import type { SportsRacePageState } from './types';

interface ThisType {
  races: Selector<RootState, MeetingBasedMarketTemplatesView['races']>;
  raceId: Selector<RootState, SportsRacePageState['raceId']>;
  markets: Selector<RootState, MeetingBasedMarketTemplatesView['markets']>;
  currentRace: Selector<RootState, RaceView | undefined>;
  timeformRunners: Selector<RootState, SportsRacePageState['timeformRunners']>;
}

export const selectors = withMeetingBasedMarketTemplateModelSelectors(
  defaultState,
  (slice, createSelector) => ({
    hasPreloaded: () => slice((state) => state.hasPreloaded),
    isLoading: () => slice((state) => state.isLoading),
    raceId: () => slice((state) => state.raceId),
    currentRace(this: ThisType) {
      return createSelector(this.races, this.raceId, (races, raceId) =>
        raceId ? races[raceId] : undefined
      );
    },
    groups: () => slice((state) => state.groups),
    errorCode: () => slice((state) => state.errorCode),
    raceWinMarket(this: ThisType) {
      return createSelector(
        this.currentRace,
        this.markets,
        (currentRace, markets) => markets[currentRace?.winMarketId ?? '']
      );
    },
    timeformRunners: () => slice((state) => state.timeformRunners),
    hasTimeformData(this: ThisType) {
      return createSelector(
        this.currentRace,
        this.timeformRunners,
        (currentRace, timeformRunners) =>
          Boolean(currentRace?.verdict) || Boolean(timeformRunners?.length)
      );
    },
    orderedRaces: () => slice((state) => sortBy(Object.values(state.races), 'startTime')),
    race(this: ThisType) {
      return createSelector(
        this.races,
        (_state: RootState, raceId: RaceId) => raceId,
        (races, raceId): RaceView | undefined => races[raceId]
      );
    },
    cachedRace: () => slice((state) => state.cachedRace),
    cachedRaces: () => slice((state) => sortBy(state.cachedRaces, 'startTime'))
  })
);
